/* ============================================================
 * ADG Art Grid — the engine behind the "Choose a Design" section.
 *
 * One card per DESIGN (not per product): flat art from the artwork
 * index, ordered by the collection's own product ranking.
 *
 * Loading behaviour (all optional, driven by section settings):
 *   A  skeleton shimmer per card until its image decodes
 *   B  first row loads eagerly; everything else stays lazy
 *   C  auto-load ahead of the scroll, bounded, button as fallback
 *   D  smaller first batch for a faster first paint
 * Set "Load more behaviour" to "Button only" to get plain
 * click-to-load back — nothing else needs changing.
 *
 * ---- Three things this file does beyond rendering ----
 *
 * 1. FORMAT CONTEXT. Reached from /collections/canvas-prints, the tile
 *    link carries ?from=canvas-prints. The section's "Format map"
 *    turns that into a product suffix, so the cards link to
 *    /products/<design>-canvas instead of -art-print and the
 *    breadcrumb points back where the shopper came from. Without this
 *    a shopper who chose Canvas Prints silently ends up on art prints.
 *
 * 2. FIND A DESIGN. The whole collection is already in memory, so
 *    search, artist and shape filtering and sorting are pure array
 *    work — no request, no page reload, no server sort. Every control
 *    hides itself unless it has at least two things to offer.
 *
 *    Deliberately NOT offered: sort by best selling. The pipeline
 *    designs were created in April 2026 with no sales history, so they
 *    all tie and the order comes out arbitrary — that is what produced
 *    "Zoo: Zebras, Zoo: Pandas, Zoo: Maccaws".
 *
 * 3. ENLARGE. Each card gets the same control the Build-Your-Own
 *    builder has, handing the CURRENT filtered list to ADGLightbox so
 *    the viewer pages through exactly what the shopper is looking at.
 * ============================================================ */
(function () {
  'use strict';

  var DEFAULT_PAGE = 60;
  var EAGER_COUNT = 6;          // roughly the first visible row
  var FALLBACK_SUFFIX = '-art-print';

  /* ---- tiny helpers (kept ES5 to match the rest of the theme) ---- */

  function query() {
    var out = {}, s = String(location.search || '').replace(/^\?/, '');
    if (!s) return out;
    var parts = s.split('&');
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i]) continue;
      var eq = parts[i].indexOf('=');
      var k = eq < 0 ? parts[i] : parts[i].slice(0, eq);
      var v = eq < 0 ? '' : parts[i].slice(eq + 1);
      try {
        out[decodeURIComponent(k)] = decodeURIComponent(v.replace(/\+/g, ' '));
      } catch (e) { /* malformed escape in a hand-edited URL — skip it */ }
    }
    return out;
  }

  // "canvas-prints=canvas=Canvas Prints|metal-signs=metal-sign=Metal Signs"
  function parseFormats(raw) {
    var map = {};
    if (!raw) return map;
    raw.split('|').forEach(function (row) {
      var bits = row.split('=');
      if (bits.length < 2 || !bits[0] || !bits[1]) return;
      map[bits[0]] = { suffix: bits[1], label: bits[2] || bits[0], handle: bits[0] };
    });
    return map;
  }

  function pipeList(v) { return v ? String(v).split('|').filter(Boolean) : []; }

  // One design is five products, and the merchant picks whichever one their
  // search surfaced, so every picked handle is folded back to its design.
  var SUFFIX_RE = /-(art-print|canvas|metal-sign|notecard-set|postcard-set|puzzle)$/;
  function designHandle(productHandle) { return String(productHandle).replace(SUFFIX_RE, ''); }

  // The same global lever the collection tiles use: kills the multi-image
  // collage posters everywhere at once instead of per collection.
  function skipMatcher(raw) {
    var terms = String(raw || '').split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
    if (!terms.length) return function () { return false; };
    return function (title) {
      var t = String(title || '').toLowerCase();
      for (var i = 0; i < terms.length; i++) if (t.indexOf(terms[i]) !== -1) return true;
      return false;
    };
  }

  function shuffle(list) {
    for (var i = list.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = list[i]; list[i] = list[j]; list[j] = t;
    }
    return list;
  }

  function collator() {
    // Numeric collation so "10 Piece" doesn't sort before "2 Piece".
    if (window.Intl && Intl.Collator) {
      var c = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
      return function (a, b) { return c.compare(a, b); };
    }
    return function (a, b) { return String(a).localeCompare(String(b)); };
  }
  var cmp = collator();

  function init(root) {
    var grid = root.querySelector('[data-adg-artgrid]');
    if (!grid) return;

    var moreBtn  = root.querySelector('[data-artgrid-more]');
    var countEl  = root.querySelector('[data-df-count]');
    var sentinel = root.querySelector('[data-artgrid-sentinel]');
    var ctxEl    = root.querySelector('[data-designs-context]');
    var chipsEl  = root.querySelector('[data-df-chips]');

    var els = {
      q:        root.querySelector('[data-df-q]'),
      qWrap:    root.querySelector('[data-df-searchwrap]'),
      artist:   root.querySelector('[data-df-artist]'),
      artistWrap: root.querySelector('[data-df-artistwrap]'),
      orient:   root.querySelector('[data-df-orient]'),
      orientWrap: root.querySelector('[data-df-orientwrap]'),
      sort:     root.querySelector('[data-df-sort]'),
      sortWrap: root.querySelector('[data-df-sortwrap]')
    };

    var handle   = root.dataset.collection;
    var indexUrl = root.dataset.indexUrl;
    var PAGE  = parseInt(root.dataset.perPage, 10) || DEFAULT_PAGE;
    var FIRST = parseInt(root.dataset.firstBatch, 10) || Math.min(24, PAGE);
    var mode  = root.dataset.loadBehaviour || 'endless';
    var AUTO  = mode === 'endless' || mode === 'auto';
    var AUTO_MAX  = mode === 'endless' ? Infinity : (parseInt(root.dataset.autoBatches, 10) || 8);
    var MAX_ITEMS = parseInt(root.dataset.maxAutoItems, 10) || 750;
    var ZOOM = root.dataset.zoom === '1' && !!window.ADGLightbox;
    var URL_STATE = root.dataset.urlState === '1';
    var IS_MAIN = root.dataset.main === '1';
    var SHOW_ARTIST = root.dataset.showArtist === '1';
    var ORDER = root.dataset.order || 'featured';
    // The section's own "Default order". Kept so the two places that react to
    // "the shopper has chosen a sort" can tell a real choice from the default
    // sitting in state — otherwise setting any default at all lights the sort
    // control orange on every page and writes ?sort= into every URL.
    var DEFAULT_SORT = root.dataset.defaultSort || '';
    var LIMIT = parseInt(root.dataset.limit, 10) || 0;
    // Locked artist (the individual artist pages): the grid is permanently
    // scoped to one artist — a comma list, because index credits can differ
    // from the display name ("Annie E. Cauffman"). The artist control and
    // chip never render, the URL never carries ?artist=, and nothing the
    // shopper does can widen it.
    var LOCK_ARTISTS = (root.dataset.lockArtist || '').split(',')
      .map(function (s) { return s.trim(); }).filter(Boolean);
    var LOCK_ARTIST = LOCK_ARTISTS.length > 0;
    // Solo lock (Joel, 08-31): he co-signs nearly every design, which buried
    // his own work under everyone else's. With data-lock-solo the grid keeps
    // only designs where the locked artist is the SOLE credit.
    var LOCK_SOLO = LOCK_ARTIST && root.dataset.lockSolo === '1';
    var skip = skipMatcher(root.dataset.skipTitles);
    var pickHandles = pipeList(root.dataset.picks).map(designHandle);
    var blockedSet = {};
    pipeList(root.dataset.blocked).forEach(function (h) { blockedSet[designHandle(h)] = 1; });

    // Only the page's own picker reads and writes the address bar. Two
    // instances on one page would otherwise both claim ?q= and overwrite
    // each other on every keystroke.
    var params  = IS_MAIN ? query() : {};
    var formats = parseFormats(root.dataset.formats);
    var fmt     = (params.from && formats[params.from]) || null;
    var suffix  = fmt ? '-' + fmt.suffix : FALLBACK_SUFFIX;
    // Landscape hold (global setting "adg_landscape_hold_formats", a comma
    // list of format suffixes, e.g. "canvas"): while a format is held,
    // landscape designs do not exist in that format's picking context —
    // no cards, no filter counts, no pins. Flip OFF in the theme editor
    // (clear the setting) the day the vendor approves; pairs with
    // scripts/launch/holds/hold-landscape-canvases.mjs (store side).
    var holdFormats = (root.dataset.holdLandscape || '').split(',')
      .map(function (t) { return t.trim(); }).filter(Boolean);
    var HIDE_LANDSCAPE = !!(fmt && holdFormats.indexOf(fmt.suffix) > -1);
    // Availability-first: when the index row carries `f` (the formats whose
    // products are published RIGHT NOW), that is the truth for this format
    // context — publish/unpublish anything and the grid follows at the next
    // hourly index build. The orientation rule above remains only as the
    // fallback for rows from an older index file.
    function availableIn(a) {
      if (!fmt || !a) return true;
      if (a.f) return a.f.indexOf(fmt.suffix) > -1;
      return !(HIDE_LANDSCAPE && (a.o || '') === 'landscape');
    }
    if (!IS_MAIN) URL_STATE = false;

    var all = [];          // every design in this collection, Featured order
    var view = [];         // what the filters/sort currently leave
    var shown = 0;   // mirrored from the core, for updateCount and the viewer

    // "New" badge window (global theme setting, days; 0 = off). ISO date
    // strings compare correctly, so the cutoff is just a string. The date
    // judged is ADGFilters.pubDate — the artwork's TRUE original date (the
    // custom.published_at metafield baked into the index as `d`), the same
    // field the Date sorts rank by. Judging the STORE publish date here
    // badged re-published old art "New" (a 2019 Zion print, 08-08) and broke
    // the invariant that badges lead "new to old".
    var NEW_DAYS = parseInt(root.dataset.newDays, 10) || 0;
    var newCutoff = NEW_DAYS > 0
      ? new Date(Date.now() - NEW_DAYS * 86400000).toISOString().slice(0, 10)
      : '';
    function isNew(a) {
      return !!(newCutoff && window.ADGFilters.pubDate(a) >= newCutoff);
    }

    var state = {
      q:      params.q || '',
      // Multi-select: a comma list in the URL, an array everywhere else.
      artist: LOCK_ARTIST ? LOCK_ARTISTS.slice()
            : (params.artist ? params.artist.split(',').filter(Boolean) : []),
      orient: params.shape || '',
      // A shared link's explicit sort wins; otherwise the merchant's
      // per-page "Default order" setting (Featured or Best selling).
      sort:   params.sort || root.dataset.defaultSort || ''
    };

    // A merged merch row carries its own URL (`purl`); designs derive theirs
    // from the handle plus the entry format.
    //
    // `purl`, NOT `u`: in the artwork index `u` is an IMAGE url — the
    // fallback for a design the pipeline never gave a CDN segment
    // (adg-index.js:42 reads it as exactly that). Reading `u` here as a
    // product url meant every such design rendered a card linking to its own
    // JPEG instead of the product. Four live designs did (saint-gaudens,
    // marsh-billings, i-d-rather-be-playing-golf, mo-visit-missouri), and
    // any future unprocessed design would have joined them silently.
    function productUrl(a) { return a.purl || ('/products/' + a.h + suffix); }

    /* ---- merged search (the /search page) ------------------------------
       data-merge-search: Shopify's product matches (view=raw JSON feed)
       join the design matches as ordinary rows in the SAME grid — no
       designs-vs-products seam on screen. Refetched, debounced, whenever
       the query changes; dropped rows whose handle is a design already in
       the index, so the two sources can never show the same thing twice. */
    var MERGE = root.dataset.mergeSearch === '1';
    var COUNT_NOUN = root.dataset.countNoun || 'design';
    var baseAll = [];        // the designs; merch is appended per query
    var indexSet = {};       // design handles, for dedup
    var merchQ = null, merchTimer = null;

    function recompose(rows) {
      all = baseAll.concat(rows || []);
      applyFilters();
    }

    function ensureMerch() {
      if (!MERGE) return;
      var q = (state.q || '').trim();
      if (q === merchQ) return;
      merchQ = q;
      clearTimeout(merchTimer);
      if (!q) { recompose([]); return; }
      merchTimer = setTimeout(function () {
        var thisQ = q;
        fetch('/search?q=' + encodeURIComponent(thisQ) + '&type=product&view=raw',
          { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : []; })
          .catch(function () { return []; })
          .then(function (rows) {
            if (thisQ !== merchQ) return;   // superseded by more typing
            var merch = [];
            (rows || []).forEach(function (r) {
              if (!r || !r.u || !r.t) return;
              var h = designHandle(String(r.u).split('?')[0].split('/').pop());
              if (indexSet[h]) return;      // that's a design; the index has it
              // m:1 marks a server-matched row: Shopify already matched it
              // for this query (often in its description, which the grid
              // cannot see), so the client token filter must not drop it —
              // relevance scoring sinks weak matches to the end instead.
              merch.push({ t: r.t, a: '', o: '', y: '', c: [], k: '', m: 1,
                           img: r.img, img_full: r.imgf || r.img,
                           purl: r.u, pr: r.pr });
            });
            recompose(merch);
          });
      }, 300);
    }

    /* ---- cards ---------------------------------------------------- */

    function card(a, eager, position) {
      // Markup comes from the shared factory (assets/adg-card.js) so this
      // grid and the BYO builder cannot drift apart again. Everything that
      // was special about this card is now an option, not a second copy:
      //   mode 'link'   — it navigates to the product
      //   expand 'wrap' — a button cannot live inside an anchor (invalid,
      //                   and the anchor eats the click), so the control
      //                   goes in a sibling wrapper. Unchanged behaviour.
      var expandBtn = null;
      if (ZOOM) {
        expandBtn = window.ADGLightbox.expandButton(a.t);
        expandBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          openViewer(position);
        });
      }
      return window.ADGCard.create(
        // `cdn` is what lets the card ask for the right rung and WebP; drop
        // it here and every card silently falls back to the baked 512.jpg.
        { title: a.t, artist: a.a, img: a.img, cdn: a.cdn,
          url: productUrl(a), isNew: isNew(a) },
        {
          mode: 'link',
          eager: eager,
          showArtist: SHOW_ARTIST,
          expand: ZOOM ? 'wrap' : false,
          expandButton: expandBtn
        }
      );
    }

    function openViewer(position) {
      // Hand over the WHOLE filtered list, not just what has rendered, so the
      // arrows keep working past the bottom of the loaded grid. The viewer
      // preloads its neighbours, so paging stays instant.
      var list = view.map(function (a) {
        return {
          image: a.cdn ? a.cdn + '/1024.webp' : (a.img_full || a.img),
          // The 32px render the pipeline already makes, 593 bytes. The
          // viewer blurs it up while the full image arrives, so opening a
          // picture shows the picture rather than an empty white stage.
          lqip: a.cdn ? a.cdn + '/32.jpg' : '',
          title: a.t,
          artist: a.a,
          href: productUrl(a),
          ctaLabel: fmt ? ('View this design in ' + fmt.label) : 'View this design'
        };
      });
      window.ADGLightbox.open(list, position);
    }

    /* ---- rendering ------------------------------------------------ */

    /* Paging, the auto-load budget and scroll memory all live in
       assets/adg-grid-core.js so the builders run the identical code — see
       the note at the top of that file for why this was the last fork worth
       closing. This grid keeps only what is genuinely its own: what a card
       looks like and what clicking one does. */
    var core = window.ADGGridCore.create({
      grid: grid,
      sentinel: sentinel,
      moreBtn: moreBtn,
      firstBatch: FIRST,
      pageSize: PAGE,
      auto: AUTO,
      autoBatches: AUTO_MAX,
      maxItems: MAX_ITEMS,
      eagerCount: EAGER_COUNT,
      renderCard: function (a, index, eager) { return card(a, eager, index); },
      onRender: function (n) { shown = n; updateCount(); },
      emptyNode: function () {
        var none = document.createElement('p');
        none.className = 'adg-designs__none';
        none.textContent = 'No designs match. Try clearing a filter.';
        return none;
      }
    });

    function updateCount() {
      if (!countEl) return;
      var n = view.length;
      var word = n === 1 ? COUNT_NOUN : COUNT_NOUN + 's';
      var text = (n === all.length) ? n + ' ' + word : n + ' of ' + all.length + ' ' + word;
      // Say what format you're looking at. The breadcrumb alone is too quiet —
      // a shopper who came in through Canvas Prints is now looking at a page of
      // artwork with no other sign that these are canvases.
      if (fmt) text += ' · shown as ' + fmt.label;
      countEl.textContent = text;
    }

    function fail(msg) {
      grid.innerHTML = '<p class="adg-coll__empty">' + msg + '</p>';
      if (moreBtn) moreBtn.hidden = true;
      if (sentinel) sentinel.hidden = true;
    }

    /* ---- filtering and sorting ------------------------------------ */

    // A control that is narrowing the set says so. Orange at rest means the
    // control exists; orange filled means it is doing something, which is
    // also how you spot the filter you forgot you set.
    function markActive() {
      if (els.artistWrap && !els.artistWrap.classList.contains('adg-coll__field--static')) {
        els.artistWrap.classList.toggle('is-active', !!state.artist);
      }
      if (els.orientWrap) els.orientWrap.classList.toggle('is-active', !!state.orient);
      if (els.sortWrap) els.sortWrap.classList.toggle('is-active', !!state.sort && state.sort !== DEFAULT_SORT);
    }

    function applyFilters(firstLoad) {
      markActive();
      // Shared pipeline (assets/adg-filters.js) — the BYO builder runs the
      // same one, so "filter by artist" cannot mean two different things on
      // two pages. Chips and URL sync stay here; paging is adg-grid-core.
      view = window.ADGFilters.filterAndSort(all, state);
      core.setItems(view, !!firstLoad);
      renderChips();
      syncUrl();
      ensureMerch();   // merged-search pages refresh their merch rows;
                       // no-op everywhere else and when the query is unchanged
    }

    function renderChips() {
      // ONE renderer, shared with the builders (adg-filters.js) — the pills
      // cannot behave differently on two surfaces again.
      if (LOCK_ARTIST) {
        // The locked artist is the page, not a filter — no removable chip.
        var held = state.artist;
        state.artist = [];
        window.ADGFilters.renderChips(chipsEl, els, state, function () { applyFilters(); });
        state.artist = held;
        return;
      }
      window.ADGFilters.renderChips(chipsEl, els, state, function () { applyFilters(); });
    }

    var urlTimer = null;
    function syncUrl() {
      if (!URL_STATE || !window.history || !history.replaceState) return;
      // Debounced: typing in the search box must not write the address bar on
      // every keystroke. replaceState (not pushState) on purpose — a filtered
      // view stays shareable and bookmarkable without burying the page the
      // shopper came from under twenty history entries.
      clearTimeout(urlTimer);
      urlTimer = setTimeout(function () {
        var p = [];
        if (params.from)  p.push('from=' + encodeURIComponent(params.from));
        if (state.q)      p.push('q=' + encodeURIComponent(state.q));
        if (state.artist.length && !LOCK_ARTIST) p.push('artist=' + encodeURIComponent(state.artist.join(',')));
        if (state.orient) p.push('shape=' + encodeURIComponent(state.orient));
        if (state.sort && state.sort !== DEFAULT_SORT) p.push('sort=' + encodeURIComponent(state.sort));
        try {
          history.replaceState(null, '', location.pathname + (p.length ? '?' + p.join('&') : '') + location.hash);
        } catch (e) { /* some in-app browsers refuse; the page still works */ }
      }, 300);
    }

    /* ---- the controls, built from what this collection actually holds -- */

    function buildControls() {
      // What each control offers — and when it hides — is ONE rulebook,
      // ADGFilters.populateRow, shared with the BYO builders. It hands back
      // the state it could honour: a remembered artist who is not in this
      // collection comes back cleared, and this grid adopts that.
      var norm = window.ADGFilters.populateRow(els, all, state);
      state.artist = norm.artist;
      if (LOCK_ARTIST) {
        // The lock survives normalisation and the control stays gone.
        state.artist = LOCK_ARTISTS.slice();
        if (els.artistWrap) els.artistWrap.hidden = true;
      }
      state.orient = norm.orient;
      state.q = norm.q;
      state.sort = norm.sort;

      if (els.q) {
        els.q.addEventListener('input', function () {
          state.q = els.q.value;
          applyFilters();
        });
      }
      if (els.artist) {
        // "Add an artist": each pick becomes a chip and the dropdown snaps
        // back to the placeholder, ready for the next (Justin's UX, 08-08).
        els.artist.addEventListener('change', function () {
          var v = els.artist.value;
          if (v && state.artist.indexOf(v) === -1) state.artist.push(v);
          els.artist.value = '';
          applyFilters();
        });
      }
      if (els.orient) {
        els.orient.addEventListener('change', function () {
          state.orient = els.orient.value;
          applyFilters();
        });
      }
      if (els.sort) {
        els.sort.addEventListener('change', function () {
          state.sort = els.sort.value;
          applyFilters();
        });
      }
    }

    /* ---- "you came in through Canvas Prints" ---------------------- */

    // "Puzzles" is not in the Format map and never will be until per-design
    // puzzles exist, so there is no collection title to borrow. The handle is
    // enough: these origins are always product-format collections, whose
    // handles read cleanly once hyphens become spaces.
    function titleFromHandle(h) {
      return h.split('-').map(function (w) {
        return w ? w.charAt(0).toUpperCase() + w.slice(1) : w;
      }).join(' ');
    }

    /* WHERE YOU CAME FROM IS NOT THE SAME QUESTION AS WHAT FORMAT YOU ARE
       SHOPPING, and fusing them is what broke this. The back control was
       gated on the Format map, so arriving from Puzzles — which is
       deliberately not in the map — left the shopper with no way back.
       Routing needs the map. Going back needs only the handle. */
    function setBackControl() {
      var el = document.querySelector('[data-adg-actionbar-back]');
      if (!el) return;
      var lbl = el.querySelector('.adg-actionbar__label');

      if (params.from) {
        el.dataset.href = '/collections/' + params.from;
        delete el.dataset.history;
        if (lbl) lbl.textContent = fmt ? fmt.label : titleFromHandle(params.from);
        el.hidden = false;
        return;
      }

      // No origin in the URL — Shop by Collection, the homepage and the
      // builders link here without one. Hand the navigation to the browser
      // (which restores the scroll for free), but NAME the destination: a
      // button that just says "Back" makes the shopper work out where they
      // are, and it reads as a different control from the one that says
      // "Metal Signs" two pages earlier.
      //
      // The name comes from the H1 of the page they left, recorded when they
      // clicked the tile — so it is that page's real title rather than a
      // title-cased slug. Falls back to "Back" only when there is nothing to
      // go on, which is better than naming the wrong place.
      var ref = document.referrer;
      if (ref && ref.indexOf(window.location.origin) === 0 &&
          ref.split('#')[0] !== window.location.href.split('#')[0] &&
          window.history.length > 1) {
        var origin = window.ADGScrollMemory && window.ADGScrollMemory.origin();
        var refPath = ref.slice(window.location.origin.length).split('?')[0];
        delete el.dataset.href;
        el.dataset.history = '1';
        if (lbl) {
          lbl.textContent = (origin && origin.label && origin.path === refPath)
            ? origin.label
            : 'Back';
        }
        el.hidden = false;
      }
    }

    function showContext() {
      // Only the instance that IS this collection page may speak for it. A
      // second picker embedded further down the page must not rewrite the
      // breadcrumb out from under the first.
      if (!IS_MAIN) return;

      // The bottom bar first, and unconditionally: a breadcrumb sits at the
      // top of a page you have just scrolled a long way down, so on its own
      // it is a way back that is nowhere near you when you want it.
      setBackControl();

      if (!fmt) {
        // Known origin, no format mapping (Puzzles). The breadcrumb should
        // still say where you came from rather than the generic "Shop Art".
        if (params.from) {
          var c0 = document.querySelector('.adg-coll__breadcrumb a');
          if (c0) {
            c0.textContent = titleFromHandle(params.from);
            c0.href = '/collections/' + params.from;
          }
        }
        return;
      }
      // Prefer rewriting the breadcrumb the header section already renders —
      // one place saying where you are beats two. The standalone line is the
      // fallback for when breadcrumbs are switched off.
      var crumbLink = document.querySelector('.adg-coll__breadcrumb a');
      if (crumbLink) {
        crumbLink.textContent = fmt.label;
        crumbLink.href = '/collections/' + fmt.handle;
        return;
      }
      if (!ctxEl) return;
      ctxEl.innerHTML = '';
      var a = document.createElement('a');
      a.href = '/collections/' + fmt.handle;
      a.textContent = '← Back to ' + fmt.label;
      ctxEl.appendChild(a);
      ctxEl.hidden = false;
    }

    /* ---- boot ------------------------------------------------------ */

    // SKELETONS BEFORE DATA. The builders feel instant because their index
    // fetch hides behind the collection-tiles step; a design page fetches
    // 1.2MB the moment you arrive, and used to show "Loading artwork…" text
    // until everything rendered in one chug (Justin, 08-05 — and he is
    // right that it is the same card component; only the data timing
    // differed). So: paint the whole first batch as shimmer-boxes NOW.
    // core.setItems() clears the grid when the real cards arrive, and the
    // fail() path replaces them with its message — nothing else changes.
    (function paintSkeletons() {
      var n = FIRST;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < n; i++) {
        var sk = document.createElement('div');
        sk.className = 'adg-card adg-card--skeleton';
        sk.innerHTML = '<div class="adg-card__media is-loading"></div><span class="adg-card__title">&nbsp;</span>';
        frag.appendChild(sk);
      }
      grid.innerHTML = '';
      grid.appendChild(frag);
    })();

    Promise.all([
      // One shared load for the whole page — the tiles section and this grid
      // used to fetch the same file concurrently, and the browser cache does
      // not de-duplicate requests already in flight.
      window.ADGIndex.load((indexUrl || '').trim()),
      // No collection handle = standalone mode (the search page): the whole
      // index is the scope and there is no collection order to fetch.
      handle
        ? fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250',
            { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : { products: [] }; })
            .catch(function () { return { products: [] }; })
        : Promise.resolve({ products: [] })
    ]).then(function (res) {
      var everything = {};                 // the whole index, for resolving pins
      var byHandle = {};                   // just this collection
      // Rows arrive already expanded to the flat shape this file has always
      // read (a, ah, o, c, img, img_full); meta carries the image URL rule.
      var indexMeta = res[0].meta;
      (res[0].rows || []).forEach(function (a) {
        everything[a.h] = a;
        if (LOCK_SOLO) {
          var credited = a.artists || [];
          if (credited.length !== 1 || LOCK_ARTISTS.indexOf(credited[0]) === -1) return;
        }
        if (!handle || (a.c || []).indexOf(handle) !== -1) byHandle[a.h] = a;
      });

      // The collection's own product order decides the default "Collection
      // order" ranking. products.json caps at 250 products and the pipeline
      // makes five per design, so only the first ~50 designs get that ranking
      // and the rest follow in index order. Pre-existing, and worth revisiting
      // if that order ever has to be exact all the way down.
      var ordered = [], seen = {};
      (res[1].products || []).forEach(function (p) {
        var awh = designHandle(p.handle);
        if (byHandle[awh] && !seen[awh]) { seen[awh] = 1; ordered.push(byHandle[awh]); }
      });
      Object.keys(byHandle).forEach(function (h) { if (!seen[h]) ordered.push(byHandle[h]); });

      // Curation: blocked comes out; everything else STAYS — the collage
      // posters are items for sale (Justin, 08-08: "filtering them out is
      // not the right move"). They get DEMOTED to the end of the default
      // order further down, and behave like any design under explicit sorts.
      var pool = ordered.filter(function (a) {
        return !blockedSet[a.h];
      });

      switch (ORDER) {
        case 'az':      pool.sort(function (x, y) { return cmp(x.t, y.t); }); break;
        case 'za':      pool.sort(function (x, y) { return cmp(y.t, x.t); }); break;
        case 'new':     pool.sort(function (x, y) { return cmp(window.ADGFilters.pubDate(y), window.ADGFilters.pubDate(x)) || (y.y || 0) - (x.y || 0) || cmp(x.t, y.t); }); break;
        case 'old':     pool.sort(function (x, y) { return (x.y || 0) - (y.y || 0) || cmp(x.t, y.t); }); break;
        case 'shuffle': shuffle(pool); break;
        default: break;                    // 'featured' — leave the collection's order alone
      }

      if (fmt) pool = pool.filter(availableIn);

      var pinned = [], pinnedSet = {};
      pickHandles.forEach(function (h) {
        var a = byHandle[h] || everything[h];   // a pin from outside the collection still works
        if (!availableIn(a)) return;
        if (a && !pinnedSet[a.h]) { pinnedSet[a.h] = 1; pinned.push(a); }
      });
      all = pinned.concat(pool.filter(function (a) { return !pinnedSet[a.h]; }));

      // Demotion, not exclusion: titles on the demote list drop to the END
      // of the default Featured order (pins are never demoted). Explicit
      // sorts run through the shared pipeline over this same array, so A–Z,
      // Best selling and the rest place these designs naturally.
      var demoted = all.filter(function (a) { return skip(a.t) && !pinnedSet[a.h]; });
      if (demoted.length) {
        all = all.filter(function (a) { return !skip(a.t) || pinnedSet[a.h]; }).concat(demoted);
      }

      if (LIMIT > 0 && all.length > LIMIT) all = all.slice(0, LIMIT);

      baseAll = all;            // merged search appends merch onto this
      indexSet = everything;    // and dedups against it

      grid.innerHTML = '';
      if (!all.length) return fail('No artwork found in this collection yet.');

      // Coming back from a product. `recall` is one-shot and guarded, so it
      // only answers on a genuine Back to this exact URL — filters included,
      // since they live in the query string and so the redrawn set matches
      // what was on screen. Only the grid that IS this page may claim it.
      // Coming back from a product: the core redraws the run the shopper had
      // BEFORE anything scrolls, so there is never a short page to land on.
      // Guarded so only the grid that IS this page may claim the return.
      var backY = IS_MAIN ? core.claimReturn() : 0;

      showContext();
      buildControls();
      applyFilters(true);   // renders the first batch and arms the engine
      if (backY && window.ADGScrollMemory) window.ADGScrollMemory.restore(backY);

      var bar = root.querySelector('[data-designs-bar]');
      if (bar) bar.classList.add('is-ready');
    }).catch(function (err) {
      console.error('[adg-artgrid]', err);
      fail('Could not load artwork. Try refreshing.');
    });

    if (moreBtn) moreBtn.addEventListener('click', function () { core.more(); });

    // A card is about to take the shopper to a product. Record how far down
    // they were AND how many cards were drawn — the scroll alone is useless,
    // because on the way back the grid starts at one batch and the page is a
    // third of the height it was. Capture phase so the lightbox control,
    // which stops the click, does not get counted as leaving.
    grid.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (a) core.remember(a.pathname);   // `to`: only that page can arm the return
    }, true);
  }

  document.querySelectorAll('[data-adg-designs]').forEach(init);
})();
